import React, { useState } from 'react';
import { Upload, message, Button, Card, Space, Divider, Alert } from 'antd';
import { UploadOutlined, FileOutlined } from '@ant-design/icons';
import { 
  useUploadBonlivDBF, 
  useUploadBonlivDetailsDBF, 
  useUploadBothBonlivDBF 
} from '../../hooks/bonlivHooks';

const BulkUploadBonliv = () => {
  const [bonlivFile, setBonlivFile] = useState(null);
  const [detailsFile, setDetailsFile] = useState(null);
  
  const { handleUpload: uploadBonliv, loading: loadingBonliv, error: errorBonliv, uploadResult: resultBonliv } = useUploadBonlivDBF();
  const { handleUpload: uploadDetails, loading: loadingDetails, error: errorDetails, uploadResult: resultDetails } = useUploadBonlivDetailsDBF();
  const { handleUpload: uploadBoth, loading: loadingBoth, error: errorBoth, uploadResult: resultBoth } = useUploadBothBonlivDBF();

  const handleUploadBonliv = async () => {
    if (!bonlivFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadBonliv(bonlivFile);
      message.success('Bonliv uploaded successfully!');
      setBonlivFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadDetails = async () => {
    if (!detailsFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadDetails(detailsFile);
      message.success('DetailBonliv uploaded successfully!');
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadBoth = async () => {
    if (!bonlivFile || !detailsFile) {
      message.error('Please select both DBF files!');
      return;
    }

    try {
      await uploadBoth(bonlivFile, detailsFile);
      message.success('Both files uploaded successfully!');
      setBonlivFile(null);
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const bonlivProps = {
    onRemove: () => {
      setBonlivFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setBonlivFile(file);
      return false;
    },
    fileList: bonlivFile ? [bonlivFile] : [],
    accept: '.dbf,.DBF',
  };

  const detailsProps = {
    onRemove: () => {
      setDetailsFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setDetailsFile(file);
      return false;
    },
    fileList: detailsFile ? [detailsFile] : [],
    accept: '.dbf,.DBF',
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Upload Bonliv DBF Files" style={{ marginBottom: 24 }}>
        <Alert
          message="DBF File Upload Instructions"
          description={
            <div>
              <p><strong>Upload Bonliv Header file (.dbf):</strong> Contains the main bonliv records (typically BONLIV.dbf)</p>
              <p><strong>Upload Bonliv Detail file (.dbf):</strong> Contains the detail/line items for bonliv (typically DETBONLIV.dbf)</p>
              <p><strong>Upload Both:</strong> Upload both files together (recommended)</p>
              <p style={{ marginTop: 8, color: '#52c41a' }}>
                <strong>Note:</strong> Any file name with .dbf or .DBF extension is accepted
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Upload BONLIV.dbf */}
          <Card size="small" title="1. Upload Bonliv Headers (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...bonlivProps}>
                <Button icon={<UploadOutlined />} disabled={loadingBonliv || loadingBoth}>
                  Select DBF File (Headers)
                </Button>
              </Upload>
              {bonlivFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {bonlivFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadBonliv}
                disabled={!bonlivFile || loadingBonliv || loadingBoth}
                loading={loadingBonliv}
              >
                {loadingBonliv ? 'Uploading...' : 'Upload Bonliv'}
              </Button>
              {errorBonliv && (
                <Alert message={errorBonliv} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultBonliv && (
                <Alert
                  message="Upload Successful"
                  description={resultBonliv.message || 'Bonliv seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          {/* Upload DETBONLIV.dbf */}
          <Card size="small" title="2. Upload Bonliv Details (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...detailsProps}>
                <Button icon={<UploadOutlined />} disabled={loadingDetails || loadingBoth}>
                  Select DBF File (Details)
                </Button>
              </Upload>
              {detailsFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {detailsFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadDetails}
                disabled={!detailsFile || loadingDetails || loadingBoth}
                loading={loadingDetails}
              >
                {loadingDetails ? 'Uploading...' : 'Upload Details'}
              </Button>
              {errorDetails && (
                <Alert message={errorDetails} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultDetails && (
                <Alert
                  message="Upload Successful"
                  description={resultDetails.message || 'DetailBonliv seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          <Divider>OR</Divider>

          {/* Upload Both */}
          <Card size="small" title="3. Upload Both Files Together (Recommended)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Upload {...bonlivProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Headers File (.dbf)
                  </Button>
                </Upload>
                {bonlivFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {bonlivFile.name}
                  </span>
                )}
              </div>
              <div>
                <Upload {...detailsProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Details File (.dbf)
                  </Button>
                </Upload>
                {detailsFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {detailsFile.name}
                  </span>
                )}
              </div>
              <Button
                type="primary"
                size="large"
                onClick={handleUploadBoth}
                disabled={!bonlivFile || !detailsFile || loadingBoth}
                loading={loadingBoth}
                style={{ marginTop: 16 }}
              >
                {loadingBoth ? 'Uploading Both Files...' : 'Upload Both Files'}
              </Button>
              {errorBoth && (
                <Alert message={errorBoth} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultBoth && (
                <Alert
                  message="Upload Successful"
                  description={resultBoth.message || 'Both files seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
};

export default BulkUploadBonliv;










